import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Phone, Save, Crown, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";

const getLoyalty = (count) => {
  if (count >= 5) return { label: "VIP", color: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: Crown };
  if (count >= 2) return { label: "Fid\u00e8le", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Star };
  return { label: "Nouveau", color: "bg-green-100 text-green-700 border-green-200", icon: Sparkles };
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => { API.get("/clients").then(r => setClients(r.data)).catch(() => {}); }, []);

  const filtered = clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search));

  const openDetail = async (client) => {
    setSelectedClient(client);
    try {
      const res = await API.get(`/clients/${client.id}`);
      setClientDetail(res.data);
      setNotes(res.data.notes || "");
    } catch { toast.error("Erreur"); }
  };

  const saveNotes = async () => {
    try {
      await API.put(`/clients/${selectedClient.id}`, { notes });
      toast.success("Notes sauvegard\u00e9es");
      setClients(clients.map(c => c.id === selectedClient.id ? { ...c, notes } : c));
    } catch { toast.error("Erreur"); }
  };

  const vipCount = clients.filter(c => c.visit_count >= 5).length;
  const loyalCount = clients.filter(c => c.visit_count >= 2 && c.visit_count < 5).length;
  const newCount = clients.filter(c => c.visit_count < 2).length;

  return (
    <div data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Clients</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{clients.length} clients au total</p>
        </div>
        <div className="flex gap-2 text-[10px]">
          <span className="bg-yellow-100 text-yellow-800 px-2.5 py-1 rounded-full font-semibold">{vipCount} VIP</span>
          <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-semibold">{loyalCount} Fid&egrave;les</span>
          <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-semibold">{newCount} Nouveaux</span>
        </div>
      </div>

      <div className="relative mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un client..." className="pl-10 h-11 rounded-xl" data-testid="client-search" />
      </div>

      <div className="bg-white rounded-xl border border-border">
        {filtered.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-muted-foreground">{clients.length === 0 ? "Les clients apparaissent automatiquement apr\u00e8s leur premi\u00e8re r\u00e9servation." : "Aucun r\u00e9sultat"}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((client) => {
              const loyalty = getLoyalty(client.visit_count);
              return (
                <button key={client.id} onClick={() => openDetail(client)}
                  className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-accent/20 transition-colors text-left gap-2"
                  data-testid={`client-${client.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-[#2563EB]/10 flex items-center justify-center font-heading font-bold text-[#2563EB] text-sm shrink-0">
                      {client.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{client.name}</p>
                        <Badge className={`${loyalty.color} text-[9px] font-semibold border px-1.5 py-0`}>
                          {loyalty.label}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5"><Phone size={9} /> {client.phone}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono text-sm font-bold">{client.visit_count}</p>
                    <p className="text-[9px] text-muted-foreground">visite{client.visit_count !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="w-5 text-muted-foreground/40 shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!selectedClient} onOpenChange={() => { setSelectedClient(null); setClientDetail(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">D&eacute;tail client</DialogTitle></DialogHeader>
          {clientDetail && (
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#2563EB]/10 flex items-center justify-center font-heading font-bold text-[#2563EB] text-xl">
                  {clientDetail.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-lg">{clientDetail.name}</p>
                    <Badge className={`${getLoyalty(clientDetail.visit_count).color} text-[10px] font-semibold border`}>{getLoyalty(clientDetail.visit_count).label}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1"><Phone size={12} /> {clientDetail.phone}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-accent/50 rounded-lg p-3 text-center">
                  <p className="font-mono text-2xl font-bold">{clientDetail.visit_count}</p>
                  <p className="text-[10px] text-muted-foreground">Visites</p>
                </div>
                <div className="bg-accent/50 rounded-lg p-3 text-center">
                  <p className="font-mono text-sm font-bold">{clientDetail.last_visit?.split("T")[0] || "-"}</p>
                  <p className="text-[10px] text-muted-foreground">Derni&egrave;re visite</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Notes</label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Pr&eacute;f&eacute;rences, notes, etc..." rows={3} data-testid="client-notes" />
                <Button size="sm" onClick={saveNotes} className="mt-2 rounded-full gap-1.5 bg-[#2563EB] hover:bg-blue-700" data-testid="save-notes-btn"><Save size={14} /> Sauvegarder</Button>
              </div>
              {clientDetail.appointments?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Historique</p>
                  <div className="space-y-1.5 max-h-44 overflow-y-auto">
                    {clientDetail.appointments.map(appt => (
                      <div key={appt.id} className="flex items-center justify-between text-sm bg-accent/30 rounded-lg px-3 py-2">
                        <div><p className="font-medium text-xs">{appt.service_name}</p><p className="text-[10px] text-muted-foreground">{appt.date} &middot; {appt.start_time}</p></div>
                        <Badge className={`text-[9px] border-0 ${appt.status === "completed" ? "bg-green-100 text-green-700" : appt.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                          {appt.status === "confirmed" ? "Confirm\u00e9" : appt.status === "completed" ? "Termin\u00e9" : "Annul\u00e9"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
